if (!customElements.get('media-zoom-slider')) {
  customElements.define('media-zoom-slider', class MediaZoomSlider extends HTMLElement {
    constructor() {
      super();
      this.minScale = 1;
      this.maxScale = 4;
      this.doubleTapScale = 2.5;
      this.activeIndex = 0;
      this.scale = 1;
      this.panX = 0;
      this.panY = 0;
      this.pointers = new Map();
      this.lastTap = null;
      this.pendingSwipeOverscroll = 0;
      this.swipeOverscrollThreshold = 60;
    }

    connectedCallback() {
      if (this.viewport) return;
      this.buildTrack();

      const dialog = this.closest('.product-media-modal__dialog');
      this.prevButton = dialog.querySelector('.product-media-modal__nav--prev');
      this.nextButton = dialog.querySelector('.product-media-modal__nav--next');
      if (this.prevButton) {
        this.prevButton.addEventListener('pointerup', (event) => event.stopPropagation());
        this.prevButton.addEventListener('click', () => this.go(-1));
      }
      if (this.nextButton) {
        this.nextButton.addEventListener('pointerup', (event) => event.stopPropagation());
        this.nextButton.addEventListener('click', () => this.go(1));
      }

      this.viewport.addEventListener('pointerdown', this.onPointerDown.bind(this));
      this.viewport.addEventListener('pointermove', this.onPointerMove.bind(this));
      this.viewport.addEventListener('pointerup', this.onPointerEnd.bind(this));
      this.viewport.addEventListener('pointercancel', this.onPointerEnd.bind(this));
      this.viewport.addEventListener('wheel', this.onWheel.bind(this), { passive: false });
      this.addEventListener('keydown', this.onKeyDown.bind(this));

      window.addEventListener('resize', debounce(() => this.setPosition(false), 100));

      this.updateNav();
      this.setPosition(false);
    }

    buildTrack() {
      const slides = Array.from(this.children);
      this.viewport = document.createElement('div');
      this.viewport.className = 'product-media-modal__viewport';
      this.track = document.createElement('div');
      this.track.className = 'product-media-modal__track';

      slides.forEach((slide) => {
        slide.classList.add('product-media-modal__slide');
        this.track.appendChild(slide);
      });

      this.viewport.appendChild(this.track);
      this.appendChild(this.viewport);
      this.slides = slides;
    }

    goToMediaId(mediaId) {
      const index = this.slides.findIndex((slide) => slide.getAttribute('data-media-id') === mediaId);
      if (index === -1) return;
      this.activeIndex = index;
      this.resetZoom();
      this.setPosition(false);
      this.updateActiveClasses();
      this.updateNav();
    }

    go(direction) {
      const nextIndex = this.activeIndex + direction;
      if (nextIndex < 0 || nextIndex >= this.slides.length) return false;
      this.activeIndex = nextIndex;
      this.resetZoom();
      this.setPosition(true);
      this.updateActiveClasses();
      this.updateNav();
      return true;
    }

    updateActiveClasses() {
      this.slides.forEach((slide, index) => {
        slide.classList.toggle('active', index === this.activeIndex);
      });
    }

    updateNav() {
      if (this.prevButton) this.prevButton.hidden = this.slides.length < 2 || this.activeIndex === 0;
      if (this.nextButton) this.nextButton.hidden = this.slides.length < 2 || this.activeIndex === this.slides.length - 1;
    }

    setPosition(animate) {
      this.track.style.transition = animate ? 'transform 250ms ease' : 'none';
      this.track.style.transform = `translateX(-${this.activeIndex * 100}%)`;
    }

    currentZoomImage() {
      const slide = this.slides[this.activeIndex];
      return slide ? slide.querySelector('.product-media-modal__zoom-image') : null;
    }

    resetZoom() {
      this.scale = 1;
      this.panX = 0;
      this.panY = 0;
      const slide = this.slides[this.activeIndex];
      const image = slide ? slide.querySelector('.product-media-modal__zoom-image') : null;
      if (image) image.style.transform = '';
      const wrapper = slide ? slide.querySelector('.product-media-modal__zoom') : null;
      if (wrapper) wrapper.classList.remove('is-zoomed');
    }

    applyZoom(image) {
      image.style.transform = `translate(${this.panX}px, ${this.panY}px) scale(${this.scale})`;
      const wrapper = image.closest('.product-media-modal__zoom');
      if (wrapper) wrapper.classList.toggle('is-zoomed', this.scale > 1);
    }

    setZoom(image, scale) {
      this.scale = Math.min(this.maxScale, Math.max(this.minScale, scale));
      if (this.scale === this.minScale) {
        this.panX = 0;
        this.panY = 0;
      }
      this.clampPan();
      this.applyZoom(image);
    }

    getPanBoundsX() {
      const rect = this.viewport.getBoundingClientRect();
      return Math.max(0, (rect.width * this.scale - rect.width) / 2);
    }

    getPanBoundsY() {
      const rect = this.viewport.getBoundingClientRect();
      return Math.max(0, (rect.height * this.scale - rect.height) / 2);
    }

    clampPan() {
      const maxX = this.getPanBoundsX();
      const maxY = this.getPanBoundsY();
      this.panX = Math.min(maxX, Math.max(-maxX, this.panX));
      this.panY = Math.min(maxY, Math.max(-maxY, this.panY));
    }

    toggleZoomAt(image) {
      if (this.scale > 1) {
        this.resetZoom();
      } else {
        this.panX = 0;
        this.panY = 0;
        this.setZoom(image, this.doubleTapScale);
      }
    }

    onWheel(event) {
      const image = this.currentZoomImage();
      if (!image) return;
      event.preventDefault();
      const delta = event.deltaY > 0 ? -0.25 : 0.25;
      this.setZoom(image, this.scale + delta);
    }

    getPinchDistance() {
      const points = Array.from(this.pointers.values());
      return Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y);
    }

    onPointerDown(event) {
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      this.viewport.setPointerCapture(event.pointerId);

      if (this.pointers.size === 2) {
        this.dragStart = null;
        this.pinchStartDistance = this.getPinchDistance();
        this.pinchStartScale = this.scale;
      } else if (this.pointers.size === 1) {
        this.dragStart = {
          x: event.clientX,
          y: event.clientY,
          panX: this.panX,
          panY: this.panY,
          time: Date.now()
        };
        this.dragDelta = 0;
      }
    }

    onPointerMove(event) {
      if (!this.pointers.has(event.pointerId)) return;
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      const image = this.currentZoomImage();

      if (this.pointers.size === 2 && image) {
        const distance = this.getPinchDistance();
        this.setZoom(image, this.pinchStartScale * (distance / this.pinchStartDistance));
        return;
      }

      if (!this.dragStart) return;
      const deltaX = event.clientX - this.dragStart.x;
      const deltaY = event.clientY - this.dragStart.y;
      this.dragDelta = Math.max(this.dragDelta, Math.hypot(deltaX, deltaY));

      if (this.scale > 1 && image) {
        const maxX = this.getPanBoundsX();
        const maxY = this.getPanBoundsY();
        const desiredX = this.dragStart.panX + deltaX;
        const clampedX = Math.min(maxX, Math.max(-maxX, desiredX));
        const overscrollX = desiredX - clampedX;

        this.panX = clampedX + overscrollX * 0.3;
        this.panY = Math.min(maxY, Math.max(-maxY, this.dragStart.panY + deltaY));
        this.pendingSwipeOverscroll = overscrollX;
        this.applyZoom(image);
      } else {
        this.track.style.transition = 'none';
        const offset = -this.activeIndex * this.viewport.clientWidth + deltaX;
        this.track.style.transform = `translateX(${offset}px)`;
      }
    }

    onPointerEnd(event) {
      event.stopPropagation();
      const wasSinglePointer = this.pointers.size === 1;
      this.pointers.delete(event.pointerId);
      if (this.pointers.size < 2) this.pinchStartDistance = null;
      if (this.pointers.size > 0 || !wasSinglePointer) return;

      const image = this.currentZoomImage();
      const isTap = this.dragStart && this.dragDelta < 10 && Date.now() - this.dragStart.time < 300;

      if (isTap && image) {
        const now = Date.now();
        if (this.lastTap && now - this.lastTap.time < 300 && Math.hypot(event.clientX - this.lastTap.x, event.clientY - this.lastTap.y) < 30) {
          this.toggleZoomAt(image);
          this.lastTap = null;
        } else {
          this.lastTap = { time: now, x: event.clientX, y: event.clientY };
        }
      }

      if (this.dragStart && !isTap) {
        let navigated = false;

        if (this.scale === 1) {
          const deltaX = event.clientX - this.dragStart.x;
          const threshold = this.viewport.clientWidth * 0.15;
          if (Math.abs(deltaX) > threshold) navigated = this.go(deltaX < 0 ? 1 : -1);
          if (!navigated) this.setPosition(true);
        } else if (image) {
          if (Math.abs(this.pendingSwipeOverscroll) > this.swipeOverscrollThreshold) {
            navigated = this.go(this.pendingSwipeOverscroll < 0 ? 1 : -1);
          }
          if (!navigated && this.pendingSwipeOverscroll !== 0) {
            image.style.transition = 'transform 200ms ease-out';
            this.clampPan();
            this.applyZoom(image);
            image.addEventListener('transitionend', () => { image.style.transition = ''; }, { once: true });
          }
        }
      } else if (this.scale === 1) {
        this.setPosition(true);
      }

      this.pendingSwipeOverscroll = 0;
      this.dragStart = null;
    }

    onKeyDown(event) {
      if (event.key === 'ArrowRight') this.go(1);
      if (event.key === 'ArrowLeft') this.go(-1);
      if (event.key === 'Escape') this.resetZoom();
    }
  });
}
